type OverwriteReportLike = {
  temporary_flg?: unknown;
  temporaryFlg?: unknown;
  sync_flg?: unknown;
  syncFlg?: unknown;
  department?: unknown;
  department_id?: unknown;
  departmentId?: unknown;
};

type DepartmentOptionLike = {
  enlight_sync_default?: unknown;
  department_id?: unknown;
  id?: unknown;
  value?: unknown;
  label?: unknown;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type ResolveOverwriteEnlightSyncOptions = {
  fallbackDepartment?: unknown;
  remandPreference?: boolean | null;
};

type RemandEnlightSyncPreference = {
  version: 1;
  enabled: boolean;
  savedAt: string;
};

type WeeklyReportSyncLike = {
  wr_id?: unknown;
  wrId?: unknown;
  id?: unknown;
  sync_flg?: unknown;
  syncFlg?: unknown;
};

const SYNC_OVERRIDE_STORAGE_KEY = 'enlightSyncOverrides';
const REMAND_SYNC_PREFERENCE_KEY_PREFIX = 'weekly_remand_enlight_sync_v1';

const getLocalStorage = (): StorageLike | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

const normalizeUserKey = (userId: unknown): string =>
  String(userId ?? '').trim().toLowerCase();

const normalizeWrId = (wrId: unknown): number | null => {
  const parsed = Number(wrId);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const buildRemandPreferenceKey = (
  userId: unknown,
  wrId: unknown
): string | null => {
  const userKey = normalizeUserKey(userId);
  const normalizedWrId = normalizeWrId(wrId);
  if (!userKey || normalizedWrId == null) return null;
  return `${REMAND_SYNC_PREFERENCE_KEY_PREFIX}:${encodeURIComponent(userKey)}:${normalizedWrId}`;
};

export const readEnlightSyncOverrides = (
  storage: StorageLike | null = getLocalStorage()
): Record<number, boolean> => {
  if (!storage) return {};
  try {
    const raw = storage.getItem(SYNC_OVERRIDE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    return Object.entries(parsed).reduce<Record<number, boolean>>(
      (result, [rawWrId, value]) => {
        const wrId = normalizeWrId(rawWrId);
        if (wrId != null && typeof value === 'boolean') result[wrId] = value;
        return result;
      },
      {}
    );
  } catch {
    return {};
  }
};

export const writeEnlightSyncOverride = (
  wrId: unknown,
  enabled: boolean,
  storage: StorageLike | null = getLocalStorage()
): boolean => {
  const normalizedWrId = normalizeWrId(wrId);
  if (!storage || normalizedWrId == null) return false;
  try {
    const map = readEnlightSyncOverrides(storage);
    map[normalizedWrId] = enabled;
    storage.setItem(SYNC_OVERRIDE_STORAGE_KEY, JSON.stringify(map));
    return true;
  } catch {
    return false;
  }
};

export const deleteEnlightSyncOverride = (
  wrId: unknown,
  storage: StorageLike | null = getLocalStorage()
): boolean => {
  const normalizedWrId = normalizeWrId(wrId);
  if (!storage || normalizedWrId == null) return false;
  try {
    const map = readEnlightSyncOverrides(storage);
    delete map[normalizedWrId];
    storage.setItem(SYNC_OVERRIDE_STORAGE_KEY, JSON.stringify(map));
    return true;
  } catch {
    return false;
  }
};

export const readRemandEnlightSyncPreference = (
  userId: unknown,
  wrId: unknown,
  storage: StorageLike | null = getLocalStorage()
): boolean | null => {
  const key = buildRemandPreferenceKey(userId, wrId);
  if (!storage || !key) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RemandEnlightSyncPreference>;
    return parsed?.version === 1 && typeof parsed.enabled === 'boolean'
      ? parsed.enabled
      : null;
  } catch {
    return null;
  }
};

export const writeRemandEnlightSyncPreference = (
  userId: unknown,
  wrId: unknown,
  enabled: boolean,
  storage: StorageLike | null = getLocalStorage()
): boolean => {
  const key = buildRemandPreferenceKey(userId, wrId);
  if (!storage || !key) return false;
  const payload: RemandEnlightSyncPreference = {
    version: 1,
    enabled,
    savedAt: new Date().toISOString(),
  };
  try {
    storage.setItem(key, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
};

export const deleteRemandEnlightSyncPreference = (
  userId: unknown,
  wrId: unknown,
  storage: StorageLike | null = getLocalStorage()
): boolean => {
  const key = buildRemandPreferenceKey(userId, wrId);
  if (!storage || !key) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

/**
 * Overwrite の保存結果に合わせ、表示用キャッシュを一貫した状態へ更新する。
 * 下書きでは差し戻し時の選択を保持し、公開時では週報連向けの最新値を保持する。
 */
export const persistOverwriteEnlightSyncOutcome = (
  userId: unknown,
  wrId: unknown,
  enabled: boolean,
  isDraft: boolean,
  storage: StorageLike | null = getLocalStorage()
): boolean => {
  if (isDraft) {
    deleteEnlightSyncOverride(wrId, storage);
    return writeRemandEnlightSyncPreference(userId, wrId, enabled, storage);
  }

  const stored = writeEnlightSyncOverride(wrId, enabled, storage);
  deleteRemandEnlightSyncPreference(userId, wrId, storage);
  return stored;
};

export const parseEnlightSyncDefault = (raw: unknown): boolean | null => {
  if (raw === undefined || raw === null || raw === '') return null;
  const normalized = String(raw).trim().toLowerCase();
  return (
    raw === true ||
    raw === 1 ||
    normalized === '1' ||
    normalized === 'true' ||
    normalized === 'on' ||
    normalized === 'yes' ||
    normalized === 'y'
  );
};

const isFlagEnabled = (raw: unknown): boolean => Number(raw) === 1;

/**
 * 週報連のサーバー値と、保存直後の楽観的な上書き値を統合する。
 * サーバーが上書き値へ追いついたIDは、呼び出し側でキャッシュを削除できるよう返す。
 */
export const resolveWeeklyReportEnlightSyncStates = (
  reports: readonly WeeklyReportSyncLike[],
  overrides: Readonly<Record<number, boolean>>
): { enabledList: boolean[]; confirmedOverrideWrIds: number[] } => {
  const confirmedOverrideWrIds: number[] = [];
  const enabledList = reports.map((report) => {
    const wrId = normalizeWrId(report.wr_id ?? report.wrId ?? report.id);
    const serverEnabled = isFlagEnabled(report.sync_flg ?? report.syncFlg);
    if (
      wrId == null ||
      !Object.prototype.hasOwnProperty.call(overrides, wrId)
    ) {
      return serverEnabled;
    }

    const overrideEnabled = overrides[wrId];
    if (overrideEnabled === serverEnabled) {
      confirmedOverrideWrIds.push(wrId);
    }
    return overrideEnabled;
  });

  return { enabledList, confirmedOverrideWrIds };
};

/**
 * Overwrite で表示する Enlight 同期状態を決定する。
 * 差し戻された下書きはバックエンドで sync_flg=0 になるため、
 * 課マスタがデフォルト ON の場合だけ ON を再適用する。
 */
export const resolveOverwriteEnlightSyncEnabled = (
  report: OverwriteReportLike,
  departmentOptions: readonly DepartmentOptionLike[],
  options: ResolveOverwriteEnlightSyncOptions = {}
): boolean => {
  const storedSyncEnabled = isFlagEnabled(report.sync_flg ?? report.syncFlg);
  const isDraft = isFlagEnabled(report.temporary_flg ?? report.temporaryFlg);

  if (!isDraft) return storedSyncEnabled;
  if (typeof options.remandPreference === 'boolean') {
    return options.remandPreference;
  }

  const targetDepartment = String(
    report.department ??
      report.department_id ??
      report.departmentId ??
      options.fallbackDepartment ??
      ''
  ).trim();
  if (!targetDepartment) return storedSyncEnabled;

  const hit = departmentOptions.find((option) => {
    const candidates = [
      option.department_id,
      option.id,
      option.value,
      option.label,
    ].map((value) => String(value ?? '').trim());
    return candidates.includes(targetDepartment);
  });

  const departmentDefault = parseEnlightSyncDefault(
    hit?.enlight_sync_default
  );

  // デフォルト ON の課だけを再適用する。マスタ未取得・OFFの場合は保存値を維持する。
  return departmentDefault === true ? true : storedSyncEnabled;
};
